"use client";

import { useEffect, useRef } from "react";
import {
  Activity,
  BarChart3,
  Building2,
  Clock,
  PieChart,
  TrendingUp,
  Users,
} from "lucide-react";
import { useReactToPrint } from "react-to-print";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button.tsx";

declare global {
  interface Window {
    anime: any;
  }
}

interface AnimalRecord {
  id: string;
  dgrRecipientCow: string;
  weight: number;
  bcsD0: string;
  donorCow: string;
  donorBreed: string;
  bull: string;
  name: string;
  dgrBull: string;
  embryoBreed: string;
  submissionTime: string;
  dateET: string;
  entryTime: string;
  status: string;
}

interface DataDisplayPageProps {
  data: AnimalRecord[];
}

export function DataDisplayPage({ data }: DataDisplayPageProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const summaryRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const reactToPrintFn = useReactToPrint({ contentRef });

  useEffect(() => {
    if (window.anime) {
      initializePageAnimations();
    }
  }, []);

  const initializePageAnimations = () => {
    if (!window.anime) return;

    // Page entrance animation
    window.anime({
      targets: contentRef.current,
      opacity: [0, 1],
      duration: 800,
      easing: "easeOutCubic",
    });

    // Header animation with logo bounce
    window.anime({
      targets: headerRef.current,
      opacity: [0, 1],
      translateY: [-30, 0],
      duration: 800,
      delay: 200,
      easing: "easeOutElastic(1, .8)",
    });

    // Logo pulse animation
    window.anime({
      targets: ".logo-animation",
      scale: [1, 1.1, 1],
      duration: 1000,
      delay: 600,
      easing: "easeInOutSine",
    });

    // Summary cards staggered animation
    window.anime({
      targets: ".summary-card",
      opacity: [0, 1],
      translateY: [20, 0],
      scale: [0.95, 1],
      duration: 600,
      delay: window.anime.stagger(100, { start: 400 }),
      easing: "easeOutBack(1.7)",
    });

    // Table animation with wave effect
    window.anime({
      targets: tableRef.current,
      opacity: [0, 1],
      translateY: [30, 0],
      duration: 800,
      delay: 800,
      easing: "easeOutCubic",
    });

    // Table rows animation
    window.anime({
      targets: ".table-row",
      opacity: [0, 1],
      translateX: [-20, 0],
      duration: 400,
      delay: window.anime.stagger(80, { start: 1000 }),
      easing: "easeOutCubic",
    });

    // Footer slide up
    window.anime({
      targets: footerRef.current,
      opacity: [0, 1],
      translateY: [20, 0],
      duration: 600,
      delay: 1400,
      easing: "easeOutCubic",
    });

    // Floating elements animation
    window.anime({
      targets: ".floating-element",
      translateY: [-10, 10],
      rotate: [0, 5, -5, 0],
      duration: 3000,
      direction: "alternate",
      loop: true,
      easing: "easeInOutSine",
      delay: window.anime.stagger(500),
    });
  };

  // Calculate statistics
  const totalAnimals = data.length;
  const averageWeight =
    data.reduce((sum, animal) => sum + animal.weight, 0) / totalAnimals;
  const transferredCount = data.filter(
    (animal) => animal.status === "Transferred",
  ).length;
  const pendingCount = data.filter(
    (animal) => animal.status === "Pending",
  ).length;
  const successRate = (transferredCount / totalAnimals) * 100;

  // Breed distribution
  const breedCounts = data.reduce(
    (acc, animal) => {
      acc[animal.embryoBreed] = (acc[animal.embryoBreed] || 0) + 1;

      return acc;
    },
    {} as Record<string, number>,
  );

  // BCS distribution
  const bcsCounts = data.reduce(
    (acc, animal) => {
      acc[animal.bcsD0] = (acc[animal.bcsD0] || 0) + 1;

      return acc;
    },
    {} as Record<string, number>,
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Transferred":
        return "bg-green-100 text-green-800 hover:bg-green-200";
      case "Pending":
        return "bg-yellow-100 text-yellow-800 hover:bg-yellow-200";
      default:
        return "bg-gray-100 text-gray-800 hover:bg-gray-200";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 relative overflow-hidden">
      <div
        ref={contentRef}
        className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 relative overflow-hidden"
      >
        {/* Floating background elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="floating-element absolute top-20 left-10 w-12 h-12 bg-yellow-200 rounded-full opacity-20" />
          <div className="floating-element absolute top-40 right-20 w-8 h-8 bg-indigo-200 rounded-full opacity-25" />
          <div className="floating-element absolute bottom-32 left-1/4 w-16 h-16 bg-green-200 rounded-full opacity-15" />
          <div className="floating-element absolute bottom-20 right-1/3 w-10 h-10 bg-lemon-300 rounded-full opacity-30" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto p-6 space-y-8">
          {/* Header Section */}
          <div ref={headerRef} className="text-center space-y-6">
            <div className="flex items-center justify-center space-x-4">
              <div className="logo-animation w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-full flex items-center justify-center shadow-lg">
                <Building2 className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-slate-700 to-slate-900 bg-clip-text text-transparent">
                  AgriTech Breeding Institute
                </h1>
                <p className="text-lg text-slate-600">
                  Advanced Livestock Management System
                </p>
              </div>
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-lg p-6 shadow-lg">
              <h2 className="text-2xl font-semibold text-slate-800 mb-2">
                Greenfield Farms
              </h2>
              <p className="text-slate-600">
                Madison, Wisconsin, United States
              </p>
            </div>
          </div>

          {/* Summary Statistics */}
          <div
            ref={summaryRef}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            <Card className="summary-card bg-white/90 backdrop-blur-sm border-0 shadow-lg hover:shadow-xl transition-all duration-300">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center space-x-2 text-sm font-medium text-slate-600">
                  <Users className="w-4 h-4" />
                  <span>Total Animals</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-blue-600">
                  {totalAnimals}
                </p>
                <p className="text-sm text-slate-500 mt-1">Breeding records</p>
              </CardContent>
            </Card>

            <Card className="summary-card bg-white/90 backdrop-blur-sm border-0 shadow-lg hover:shadow-xl transition-all duration-300">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center space-x-2 text-sm font-medium text-slate-600">
                  <TrendingUp className="w-4 h-4" />
                  <span>Average Weight</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-green-600">
                  {averageWeight.toFixed(0)} kg
                </p>
                <p className="text-sm text-slate-500 mt-1">
                  Across all animals
                </p>
              </CardContent>
            </Card>

            <Card className="summary-card bg-white/90 backdrop-blur-sm border-0 shadow-lg hover:shadow-xl transition-all duration-300">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center space-x-2 text-sm font-medium text-slate-600">
                  <Activity className="w-4 h-4" />
                  <span>Success Rate</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-emerald-600">
                  {successRate.toFixed(1)}%
                </p>
                <p className="text-sm text-slate-500 mt-1">
                  {transferredCount} of {totalAnimals} transferred
                </p>
              </CardContent>
            </Card>

            <Card className="summary-card bg-white/90 backdrop-blur-sm border-0 shadow-lg hover:shadow-xl transition-all duration-300">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center space-x-2 text-sm font-medium text-slate-600">
                  <Clock className="w-4 h-4" />
                  <span>Pending</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-orange-600">
                  {pendingCount}
                </p>
                <p className="text-sm text-slate-500 mt-1">Awaiting transfer</p>
              </CardContent>
            </Card>
          </div>

          {/* Breed Distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="summary-card bg-white/90 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <PieChart className="w-5 h-5" />
                  <span>Breed Distribution</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(breedCounts).map(([breed, count]) => (
                    <div
                      key={breed}
                      className="flex items-center justify-between"
                    >
                      <span className="text-sm font-medium text-slate-700 capitalize">
                        {breed}
                      </span>
                      <div className="flex items-center space-x-2">
                        <div className="w-20 h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-500 rounded-full transition-all duration-500"
                            style={{
                              width: `${(count / totalAnimals) * 100}%`,
                            }}
                          />
                        </div>
                        <span className="text-sm text-slate-600 w-8">
                          {count}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="summary-card bg-white/90 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <BarChart3 className="w-5 h-5" />
                  <span>BCS Distribution</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(bcsCounts).map(([bcs, count]) => (
                    <div
                      key={bcs}
                      className="flex items-center justify-between"
                    >
                      <span className="text-sm font-medium text-slate-700">
                        BCS {bcs}
                      </span>
                      <div className="flex items-center space-x-2">
                        <div className="w-20 h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-green-500 rounded-full transition-all duration-500"
                            style={{
                              width: `${(count / totalAnimals) * 100}%`,
                            }}
                          />
                        </div>
                        <span className="text-sm text-slate-600 w-8">
                          {count}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Data Table */}
          <Card
            ref={tableRef}
            className="bg-white/90 backdrop-blur-sm border-0 shadow-xl"
          >
            <CardHeader>
              <CardTitle className="text-2xl font-bold text-slate-800">
                Complete Breeding Records
              </CardTitle>
              <p className="text-slate-600">
                Comprehensive overview of all animals in the breeding program
              </p>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-4 px-4 font-semibold text-slate-700">
                        ID
                      </th>
                      <th className="text-left py-4 px-4 font-semibold text-slate-700">
                        Name
                      </th>
                      <th className="text-left py-4 px-4 font-semibold text-slate-700">
                        Recipient Cow
                      </th>
                      <th className="text-left py-4 px-4 font-semibold text-slate-700">
                        Weight
                      </th>
                      <th className="text-left py-4 px-4 font-semibold text-slate-700">
                        BCS
                      </th>
                      <th className="text-left py-4 px-4 font-semibold text-slate-700">
                        Donor
                      </th>
                      <th className="text-left py-4 px-4 font-semibold text-slate-700">
                        Breed
                      </th>
                      <th className="text-left py-4 px-4 font-semibold text-slate-700">
                        Bull
                      </th>
                      <th className="text-left py-4 px-4 font-semibold text-slate-700">
                        Date
                      </th>
                      <th className="text-left py-4 px-4 font-semibold text-slate-700">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((animal) => (
                      <tr
                        key={animal.id}
                        className="table-row border-b border-slate-100 hover:bg-slate-50/50 transition-colors duration-200"
                      >
                        <td className="py-4 px-4 font-medium text-slate-700">
                          {animal.id}
                        </td>
                        <td className="py-4 px-4 font-medium text-slate-800">
                          {animal.name}
                        </td>
                        <td className="py-4 px-4 text-slate-600">
                          {animal.dgrRecipientCow}
                        </td>
                        <td className="py-4 px-4 text-slate-600">
                          {animal.weight} kg
                        </td>
                        <td className="py-4 px-4 text-slate-600">
                          {animal.bcsD0}
                        </td>
                        <td className="py-4 px-4 text-slate-600">
                          {animal.donorCow}
                        </td>
                        <td className="py-4 px-4 text-slate-600 capitalize">
                          {animal.embryoBreed}
                        </td>
                        <td className="py-4 px-4 text-slate-600">
                          {animal.bull}
                        </td>
                        <td className="py-4 px-4 text-slate-600">
                          {animal.dateET}
                        </td>
                        <td className="py-4 px-4">
                          <Badge
                            className={getStatusColor(animal.status)}
                            variant="secondary"
                          >
                            {animal.status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Back Button */}
          {/*<div className="flex justify-center">*/}
          {/*  <Button*/}
          {/*    className="px-8 py-3 bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"*/}
          {/*    onClick={handleBackClick}*/}
          {/*  >*/}
          {/*    <ArrowLeft className="w-4 h-4 mr-2" />*/}
          {/*    Back to Form*/}
          {/*  </Button>*/}
          {/*</div>*/}

          {/* Footer */}
          <div ref={footerRef} className="text-center py-8">
            <Card className="bg-gradient-to-r from-green-50 to-blue-50 border-0 shadow-lg">
              <CardContent className="py-6">
                <p className="text-lg font-medium text-slate-700">
                  {
                    "Improve Nutrition and monitor the feeding. Continue the Mineralization."
                  }
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <Button onClick={() => reactToPrintFn()} />
    </div>
  );
}
